import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, CreditCard } from "lucide-react";
import api, { formatRON, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ConsultationPayment() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [info, setInfo] = useState(undefined);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const polled = useRef(false);

  const load = useCallback(() => api.get(`/consultations/${id}/payment-info`).then((r) => { setInfo(r.data); if (r.data.payment_status === "paid") setPaid(true); }).catch(() => setInfo(null)), [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { setPaid(true); toast.success("Plată reușită!"); setParams({}); return; }
        if (data.status === "expired" || attempts >= 6) { toast.error("Plata nu a fost confirmată."); setParams({}); return; }
        setTimeout(poll, 2000);
      } catch { setParams({}); }
    };
    poll();
  }, [sessionId, setParams]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/consultations/${id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  return (
    <div className="bg-slate-950 text-white min-h-screen flex items-center justify-center px-6" data-testid="consultation-payment-page">
      <div className="max-w-md w-full border border-slate-800 bg-slate-900 p-8">
        {info === undefined ? (
          <div className="text-center text-slate-400 py-10"><Loader2 className="w-7 h-7 animate-spin mx-auto" /></div>
        ) : info === null ? (
          <div className="text-center"><p className="text-slate-400">Plata nu este disponibilă pentru această consultanță.</p><Link to="/experti" className="text-orange-500 font-semibold hover:underline mt-4 inline-block">← Directorul de experți</Link></div>
        ) : paid ? (
          <div className="text-center py-6" data-testid="consultation-paid">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <h1 className="text-2xl font-black mt-4">Plată reușită!</h1>
            <p className="text-slate-400 mt-2">Consultanța cu <b className="text-white">{info.expert_name}</b> a fost achitată prin OXO. Vă mulțumim!</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-orange-500 text-xs uppercase tracking-[0.2em] font-bold"><ShieldCheck className="w-4 h-4" /> Plată consultanță prin OXO</div>
            <h1 className="text-2xl font-black mt-3">{info.expert_name}</h1>
            <p className="text-slate-400 text-sm mt-1">{info.company} · {info.issue_type}</p>
            <div className="border-t border-slate-800 mt-6 pt-6 flex items-end justify-between">
              <span className="text-slate-400 text-sm">Tarif consultanță</span>
              <span className="text-3xl font-black text-orange-500" data-testid="consultation-amount">{formatRON(info.agreed_fee)}</span>
            </div>
            <button onClick={pay} disabled={paying} className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="pay-consultation-btn">
              <CreditCard className="w-4 h-4" /> {paying ? "Se redirecționează..." : `Plătește ${formatRON(info.agreed_fee)}`}
            </button>
            <p className="text-xs text-slate-500 mt-3 text-center">Plata este procesată securizat prin Stripe. OXO mediază colaborarea.</p>
          </>
        )}
      </div>
    </div>
  );
}
